import React, { useState, useEffect } from 'react';
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell
} from 'recharts';
import {
  TrendingUp, TrendingDown, DollarSign, Users, AlertTriangle,
  Target, ArrowUpRight, ArrowDownRight
} from 'lucide-react';
import { getDashboardSummary, getForecasts } from '../services/api';
import { format } from 'date-fns';

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];

function KPICard({ title, value, trend, trendValue, icon: Icon, color }) {
  const isPositive = trend === 'up';

  return (
    <div className="kpi-card">
      <div className={`kpi-icon ${color}`}>
        <Icon size={24} />
      </div>
      <div className="kpi-content">
        <p className="kpi-label">{title}</p>
        <h3 className="kpi-value">{value}</h3>
        {trendValue && (
          <div className={`kpi-trend ${isPositive ? 'up' : 'down'}`}>
            {isPositive ? <ArrowUpRight size={16} /> : <ArrowDownRight size={16} />}
            <span>{trendValue}% from last period</span>
          </div>
        )}
      </div>
    </div>
  );
}

function Dashboard() {
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [forecasts, setForecasts] = useState([]);

  // Demo data for visualization
  const revenueData = [
    { month: 'Jan', actual: 45000, forecast: 44000 },
    { month: 'Feb', actual: 52000, forecast: 50000 },
    { month: 'Mar', actual: 48000, forecast: 51000 },
    { month: 'Apr', actual: 61000, forecast: 58000 },
    { month: 'May', actual: 55000, forecast: 56000 },
    { month: 'Jun', actual: 67000, forecast: 65000 },
    { month: 'Jul', forecast: 72000 },
    { month: 'Aug', forecast: 78000 },
    { month: 'Sep', forecast: 82000 },
  ];

  const demandData = [
    { category: 'Product A', demand: 1200 },
    { category: 'Product B', demand: 900 },
    { category: 'Product C', demand: 650 },
    { category: 'Product D', demand: 450 },
    { category: 'Product E', demand: 300 },
  ];

  const churnRiskData = [
    { name: 'Low Risk', value: 65, color: '#10b981' },
    { name: 'Medium Risk', value: 20, color: '#f59e0b' },
    { name: 'High Risk', value: 12, color: '#ef4444' },
    { name: 'Critical', value: 3, color: '#991b1b' },
  ];

  const alerts = [
    { id: 1, severity: 'critical', title: 'Revenue Anomaly', message: 'Unexpected drop in Q3 projections', time: '5 min ago' },
    { id: 2, severity: 'warning', title: 'Churn Risk Alert', message: '15 customers moved to high risk', time: '1 hour ago' },
    { id: 3, severity: 'info', title: 'Forecast Updated', message: 'Monthly forecast recalculated', time: '2 hours ago' },
  ];

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const [summary, forecastData] = await Promise.all([
        getDashboardSummary().catch(() => null),
        getForecasts({ status: 'completed' }).catch(() => ({ forecasts: [] }))
      ]);

      setDashboardData(summary?.summary || null);
      setForecasts(forecastData?.forecasts || []);
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <div className="page-header">
        <h2>Analytics Dashboard</h2>
        <p>Overview of your business metrics and forecasts</p>
      </div>

      {/* KPI Cards */}
      <div className="kpi-grid">
        <KPICard
          title="Projected Revenue"
          value={formatCurrency(782000)}
          trend="up"
          trendValue="12.5"
          icon={DollarSign}
          color="blue"
        />
        <KPICard
          title="Forecast Accuracy"
          value="94.2%"
          trend="up"
          trendValue="2.3"
          icon={Target}
          color="green"
        />
        <KPICard
          title="At-Risk Customers"
          value="47"
          trend="down"
          trendValue="8.1"
          icon={Users}
          color="yellow"
        />
        <KPICard
          title="Active Alerts"
          value="3"
          trend="down"
          trendValue="25"
          icon={AlertTriangle}
          color="red"
        />
      </div>

      {/* Charts Row */}
      <div className="grid-2" style={{ marginBottom: '1.5rem' }}>
        {/* Revenue Forecast Chart */}
        <div className="chart-container">
          <div className="chart-header">
            <h3 className="chart-title">Revenue Forecast</h3>
            <span className="badge badge-success">
              <TrendingUp size={14} style={{ marginRight: 4 }} />
              Trending Up
            </span>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={revenueData}>
              <defs>
                <linearGradient id="colorActual" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="colorForecast" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="month" stroke="#6b7280" fontSize={12} />
              <YAxis stroke="#6b7280" fontSize={12} tickFormatter={(v) => `$${v/1000}k`} />
              <Tooltip
                formatter={(value) => formatCurrency(value)}
                contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
              />
              <Legend />
              <Area
                type="monotone"
                dataKey="actual"
                stroke="#3b82f6"
                fillOpacity={1}
                fill="url(#colorActual)"
                name="Actual"
                strokeWidth={2}
              />
              <Area
                type="monotone"
                dataKey="forecast"
                stroke="#10b981"
                fillOpacity={1}
                fill="url(#colorForecast)"
                name="Forecast"
                strokeWidth={2}
                strokeDasharray="5 5"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Demand Prediction Chart */}
        <div className="chart-container">
          <div className="chart-header">
            <h3 className="chart-title">Demand by Product</h3>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={demandData} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis type="number" stroke="#6b7280" fontSize={12} />
              <YAxis dataKey="category" type="category" stroke="#6b7280" fontSize={12} width={80} />
              <Tooltip
                contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
              />
              <Bar dataKey="demand" fill="#8b5cf6" radius={[0, 4, 4, 0]} name="Demand Units" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Bottom Row */}
      <div className="grid-2">
        {/* Churn Risk Distribution */}
        <div className="chart-container">
          <div className="chart-header">
            <h3 className="chart-title">Customer Churn Risk Distribution</h3>
          </div>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <ResponsiveContainer width="60%" height={250}>
              <PieChart>
                <Pie
                  data={churnRiskData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={90}
                  paddingAngle={2}
                  dataKey="value"
                >
                  {churnRiskData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value) => `${value}%`}
                  contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                />
              </PieChart>
            </ResponsiveContainer>
            <div style={{ flex: 1 }}>
              {churnRiskData.map((item, index) => (
                <div key={index} style={{ display: 'flex', alignItems: 'center', marginBottom: '0.75rem' }}>
                  <div style={{
                    width: 12,
                    height: 12,
                    borderRadius: 4,
                    backgroundColor: item.color,
                    marginRight: '0.75rem'
                  }} />
                  <span style={{ flex: 1, color: '#374151', fontSize: '0.875rem' }}>{item.name}</span>
                  <span style={{ fontWeight: 600, color: '#111827' }}>{item.value}%</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Alerts Panel */}
        <div className="alerts-panel">
          <div style={{ padding: '1rem 1.5rem', borderBottom: '1px solid #e5e7eb' }}>
            <h3 className="chart-title">Recent Alerts</h3>
          </div>
          {alerts.map((alert) => (
            <div key={alert.id} className="alert-item">
              <div className={`alert-icon ${alert.severity}`}>
                <AlertTriangle size={18} />
              </div>
              <div className="alert-content">
                <p className="alert-title">{alert.title}</p>
                <p className="alert-message">{alert.message}</p>
              </div>
              <span className="alert-time">{alert.time}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
